import React, { FC, JSX, useEffect, useState } from "react";
import { ServicingProvider } from "@app/projects/results/servicing-provider";
import { NetworkServicingProviderTerminationModel } from "@app/provider-terminations/networkServicingProviderTerminationModel";
import ProviderTerminationService from "@app/provider-terminations/providerTerminationService";
import UpdateProviderTermination from "@app/provider-terminations/updateProviderTermination";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { FlyoutContextOutgoingProps, FlyoutFooter, withFlyoutContext } from "@app/shared/Flyouts";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { AccessPointForProjection, CrossSpecialtyAndNetworkType } from "@app/projects/results/projection-provider";
import { ProjectionProviderIdentifier } from "@app/projects/projection-provider/projectionProviderDetailsReducer";
import { getGenericErrorMessage } from "@app/shared/utility/getGenericErrorMessage";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESRadioButton } from "@app/shared/qes-standards/forms/RadioButton";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextArea } from "@app/shared/qes-standards/forms/TextArea";
import Roles from "@app/user/roles";
import { UserContextProps, withUserContext } from "@app/user/UserContext";
import { PciDashboardTerminationDetails } from "@app/projects/network-assessment/pci-dashboard/claims-activity/PciDashboardTerminationDetails";

export enum TerminatePanelMode {
  Create = "create",
  Edit = "edit",
  View = "View",
}

export interface TerminateProps {
  onSubmit: () => void;
  projectId: number;
  specialtyGroupCode?: string;
  crossSpecialtyAndNetwork?: CrossSpecialtyAndNetworkType;
  selectedProviders?: ServicingProvider[] | AccessPointForProjection[];
  terminatePanelMode: TerminatePanelMode;
  termination?: NetworkServicingProviderTerminationModel | PciDashboardTerminationDetails;
  npis?: string[];
  showAffiliationsInsights: boolean;
  hideAllLocationsOption?: boolean;
  hideAllSpecialtiesOption?: boolean;
  isZipProject?: boolean;
  showNetworkIntegrityReason?: boolean;
}

const getDefaultIdentifier = (selectedProviders: ServicingProvider[] | AccessPointForProjection[], termination: NetworkServicingProviderTerminationModel | PciDashboardTerminationDetails, npis: string[], projectionBy?:
  ProjectionProviderIdentifier): string => {
  if (projectionBy) {
    return projectionBy;
  }
  if (termination) {
    if (termination.terminateByNPI && termination.terminateByTIN) {
      return "npitin";
    }
    if (termination.terminateByTIN) {
      return "tin";
    }
    if (termination.terminateByNPI2) {
      return ProjectionProviderIdentifier.NPI2;
    }
    if (termination.terminateByNPI) {
      return "npi";
    }
    if (termination.terminateByContractEntity) {
      return "contractentity";
    }
    if (termination.terminateByCCN) {
      return ProjectionProviderIdentifier.CCN;
    }
    if (!termination.terminateByNPI && !termination.terminateByTIN) {
      // Not terminated by NPI or TIN, so terminate by name
      return "name";
    }
  }

  if (selectedProviders?.every((provider) => !!provider.npi)) {
    return "npi";
  }
  if (selectedProviders?.every((provider) => !!provider.tin)) {
    return "tin";
  }
  if (selectedProviders?.every((provider) => !provider.npi && !provider.tin)) {
    return "name";
  }

  if (selectedProviders?.some((provider) => !provider.npi && !provider.tin)) {
    if (selectedProviders?.some((provider) => !!provider.npi)) {
      return "npi";
    }
    else if (selectedProviders?.some((provider) => !!provider.tin)) {
      return "tin";
    }
    else {
      // All providers are missing NPI and TIN, so terminate by name
      return "name";
    }
  }

  if (npis?.length > 0) {
    return "npi";
  }

  return null;
};

const getDisabledTooltipText = (isTinViewer: boolean): string => {
  return `All providers must have an NPI ${isTinViewer ? "or TIN" : ""} to terminate by All Locations and All Specialties.`
};

const maxLength = 1000;

type TerminatePanelProps = TerminateProps & UserContextProps & FlyoutContextOutgoingProps &
{
  setAllSpecialtiesInParent?: (allSpecialties: boolean) => void;
  setAllLocationsInParent?: (allLocations: boolean) => void;
  setIdentifierInParent?: (identifier: string) => void;
  setErrorInParent?: (hasError: boolean) => void;
  setHasUnsavedChangesInParent?: (hasChanges: boolean) => void;
};

const TerminateProvidersPanel: FC<React.PropsWithChildren<TerminatePanelProps>> = (props: TerminatePanelProps) => {
  const existingTermination = props.termination;
  const createMode = props.terminatePanelMode === TerminatePanelMode.Create;
  const viewMode = props.terminatePanelMode === TerminatePanelMode.View;
  const editMode = props.terminatePanelMode === TerminatePanelMode.Edit;
  const providerTerminationService = new ProviderTerminationService();
  const checkboxDisabled = viewMode || editMode;
  const userIsTermAdmin = props.user.roles.includes(Roles.TERM_PROVIDER_ADMIN);
  const userIsTermViewer = props.user.roles.includes(Roles.TERM_PROVIDER_VIEWER);
  const terminatingByNpi = props?.npis?.length > 0;

  const [reasons, setReasons] = useState<string[]>([]);
  const [reason, setReason] = useState(existingTermination && existingTermination.reasonName || "");
  const [comment, setComment] = useState(existingTermination && existingTermination.reasonDescription || "");
  const [allSpecialties, setAllSpecialties] = useState(props.crossSpecialtyAndNetwork?.allSpecialties ?? existingTermination?.allSpecialties ?? false);
  const [allLocations, setAllLocations] = useState(props.crossSpecialtyAndNetwork?.allLocations ?? existingTermination?.allLocations ?? false);
  const [identifier, setIdentifier] = useState(getDefaultIdentifier(props?.selectedProviders, existingTermination, props?.npis, props.crossSpecialtyAndNetwork?.projectionBy));
  const [error, setError] = useState<JSX.Element>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (userIsTermAdmin || userIsTermViewer) {
      providerTerminationService.getProviderTerminationReasons()
        .then((result) => {
          const filtered = props.showNetworkIntegrityReason ? result
            : result.filter((r) => r !== "Network Integrity");

          setReasons(filtered);
        }, () => {
          setError(<QESAlert type="danger">Something went wrong on our end.</QESAlert>);
        });
    } else {
      setReasons([]);
    }

    if (props.setAllSpecialtiesInParent)
      props.setAllSpecialtiesInParent(allSpecialties);

    if (props.setAllLocationsInParent)
      props.setAllLocationsInParent(allLocations);

    if (props.setIdentifierInParent)
      props.setIdentifierInParent(identifier);
  }, []);

  useEffect(() => {
    if (props.setIdentifierInParent)
      props.setIdentifierInParent(identifier);

    if (identifier === "contractentity") {
      setAllSpecialties(true);
      setAllLocations(true);
    }
  }, [identifier]);

  useEffect(() => {
    if (props.setAllSpecialtiesInParent)
      props.setAllSpecialtiesInParent(allSpecialties);
  }, [allSpecialties]);

  useEffect(() => {
    if (props.setAllLocationsInParent)
      props.setAllLocationsInParent(allLocations);
  }, [allLocations]);

  useEffect(() => {
    if (props.setErrorInParent) {
      props.setErrorInParent(error !== null);
    }
  }, [error]);

  useEffect(() => {
    if (editMode && props.setHasUnsavedChangesInParent) {
      const isDirty = reason !== (existingTermination?.reasonName ?? '') ||
                      comment !== (existingTermination?.reasonDescription ?? '');
      props.setHasUnsavedChangesInParent(isDirty);
    }
  }, [reason, comment]);

  const tinViewer = props.user.roles.indexOf(Roles.TIN_VIEWER) > -1;

  let showNpi = false;
  let showTin = false;
  let showNpi2 = false;
  let terminateByName = false;
  let anyNameTerminations = false;
  let showContractEntity = false;
  let disableContractEntityRadioButton = false;

  if (props.selectedProviders) {
    showNpi = props.selectedProviders?.every((provider) => !!provider.npi);
    showTin = tinViewer && props.selectedProviders?.every((provider) => !!provider.tin);
    showContractEntity = props.showAffiliationsInsights && !props.isZipProject;
    disableContractEntityRadioButton = !props.selectedProviders?.some((provider) => provider.professionalAffiliationId > 0);
    anyNameTerminations = !props.isZipProject && props.selectedProviders?.some((provider) => !provider.npi && (!tinViewer || !provider.tin));
  }
  if (terminatingByNpi) {
    showNpi = true;
  }

  if(identifier === "npi2") {
    showNpi2 = true;
  }

  if (editMode && existingTermination?.terminateByContractEntity) {
    showContractEntity = true;
  }

  terminateByName = !props.isZipProject && identifier.includes("name");

  const handleSubmit = async () => {
    if ((!identifier.includes("npi") && !identifier.includes("tin") && !identifier.includes("name") && !identifier.includes("contractentity") && !identifier.includes("ccn"))
      || (reason === "Other" && comment.length === 0)) {
      return;
    }

    setIsSubmitting(true);

    try{

      if (editMode) {
        await providerTerminationService.updateProviderTermination(props.projectId, { providerTerminationProjectId: existingTermination.providerTerminationProjectId, reasonDescription: comment, reasonName: reason } as UpdateProviderTermination);
      }

      if (createMode && props.selectedProviders) {
        const submittedProviders = [...props.selectedProviders];
        const terminateBy = props.crossSpecialtyAndNetwork?.projectionBy;

        await providerTerminationService.createProviderTerminations(
          props.projectId,
          terminateBy === ProjectionProviderIdentifier.NPI ? true : (terminateBy === ProjectionProviderIdentifier.NPI2 ? false : identifier.includes("npi")),
          terminateBy === ProjectionProviderIdentifier.TIN ? true : identifier.includes("tin") && tinViewer,
          terminateBy === ProjectionProviderIdentifier.ContractEntity ? true : identifier.includes("contractentity"),
          terminateBy === ProjectionProviderIdentifier.NPI2,
          submittedProviders.map((p) => p.providerId),
          reason,
          comment.substring(0, maxLength),
          allSpecialties,
          allLocations,
          props?.specialtyGroupCode,
          props?.selectedProviders.map((provider) => {
            return { contractEntityId: provider.professionalAffiliationId, providerId: provider.providerId }
          }),
          terminateBy === ProjectionProviderIdentifier.CCN,
          props?.selectedProviders
            .map((provider) => (provider as AccessPointForProjection).ccn)
            .find((ccn) => !!ccn) ?? "");
      }

      if (createMode && terminatingByNpi) {
        await providerTerminationService.createGhostProviderTerminations(props.projectId,
          true,
          reason,
          comment.substring(0, maxLength),
          props.npis);
      }
      //Make sure to close the flyout and trigger data refresh on the parent component
      props.clearWarnOnClose();
      props.handleCloseFlyout();
      setIsSubmitting(false);
      props.onSubmit();
    }
    catch (error)
    {
      let errorMessage : JSX.Element;
      if (error.status == 500) {
        errorMessage = getGenericErrorMessage()
      } else if (error.responseText != null){
        errorMessage = <>{error.responseText}</>
      }

      setError(<QESAlert type="danger" persist={true}>{errorMessage}</QESAlert>);
    }
    
  };

  let noMatchingCriteria = "All selected providers must have NPIs or all selected providers must have TINs.";
  if (!tinViewer) {
    noMatchingCriteria = "All selected providers must have NPIs.";
  }

  if (!showNpi && !showTin && !terminateByName && !showContractEntity) {
    return (
      <>
        <p className="text-danger"><i className="fa fa-exclamation-triangle"> {noMatchingCriteria}</i></p>
      </>
    );
  }
  
  return (
    <>
      {error ? error :
        (
          <>
            <div className="mb-4">
              {!(props.hideAllLocationsOption && props.hideAllSpecialtiesOption) &&
                <p className="qes-text-input__label">Apply Termination to:</p>
              }
              {!props.hideAllLocationsOption && (
                <div className="row col-9">
                  <TooltipWrapper text={anyNameTerminations ? getDisabledTooltipText(tinViewer) : null}>
                    <QESCheckbox
                      label="All Locations"
                      testId="termination-flyout__all-locations"
                      checked={allLocations || terminatingByNpi}
                      disabled={viewMode || terminatingByNpi || anyNameTerminations || identifier === "contractentity" || identifier === ProjectionProviderIdentifier.NPI2 || identifier === ProjectionProviderIdentifier.CCN}
                      onChange={(e) => setAllLocations(e.target.checked)}
                    />
                  </TooltipWrapper>
                </div>
              )}

              {!props.hideAllSpecialtiesOption && (
                <div className="row col-9">
                  <TooltipWrapper text={anyNameTerminations ? getDisabledTooltipText(tinViewer) : null}>
                    <QESCheckbox
                      label="All Specialties"
                      checked={allSpecialties || terminatingByNpi}
                      disabled={viewMode || terminatingByNpi || anyNameTerminations || identifier === "contractentity" || identifier === ProjectionProviderIdentifier.NPI2 || identifier === ProjectionProviderIdentifier.CCN}
                      onChange={(e) => setAllSpecialties(e.target.checked)}
                      testId="termination-flyout__all-specialties"
                    />
                  </TooltipWrapper>
                </div>
              )}
            </div>
            {!terminateByName && !props.crossSpecialtyAndNetwork?.projectionBy &&
              (<div>
                <div className="row">
                  <label className="col-2 label-s d-block mb-2" htmlFor="reason">Recording Criteria:</label>
                </div>
                <div className="row">
                  <div className="col-9 ms-4">
                    {showNpi && (<div>
                      <QESRadioButton
                        label="NPI"
                        checked={identifier === "npi" || terminatingByNpi}
                        disabled={checkboxDisabled || terminatingByNpi}
                        name="identifier"
                        onChange={(e) => setIdentifier(e.target.value)}
                        value="npi"
                        testId="termination-flyout__by-npi"
                      />
                    </div>)}

                    {showTin && (<div>
                      <QESRadioButton
                        label="TIN"
                        checked={identifier === "tin"}
                        disabled={checkboxDisabled}
                        name="identifier"
                        onChange={(e) => setIdentifier(e.target.value)}
                        value="tin"
                        testId="termination-flyout__by-tin"
                      />
                    </div>)}

                    {showNpi && showTin && (<div>
                      <QESRadioButton
                        label="NPI and TIN"
                        checked={identifier === "npitin"}
                        disabled={checkboxDisabled}
                        name="identifier"
                        onChange={(e) => setIdentifier(e.target.value)}
                        value="npitin"
                        testId="termination-flyout__by-npi-and-tin"
                      />
                    </div>)}

                  {showNpi2 && (
                    <QESRadioButton
                      label="NPI (Type 2)"
                      checked={identifier === "npi2"}
                      disabled={checkboxDisabled}
                      name="identifier"
                      onChange={(e) => setIdentifier(e.target.value)}
                      value="npi2"
                      testId="termination-flyout__by-npi2"
                    />
                  )}

                  {showContractEntity && (<div className="d-flex align-items-center">
                      <QESRadioButton
                        label="Contract Entity"
                        checked={identifier === "contractentity"}
                        radioContainerClassName={"me-2"}
                        disabled={checkboxDisabled || disableContractEntityRadioButton}
                        name="identifier"
                        onChange={(e) => setIdentifier(e.target.value)}
                        value="contractentity"
                        testId="termination-flyout__by-contract-entity"
                      />
                      <TooltipWrapper text={"Selecting this will terminate all providers associated with the selected contract entities."}>
                        <QesIcon className={"mb-2"} iconName={QesIcons.InfoCircle} description="Tooltip"></QesIcon>
                      </TooltipWrapper>
                    </div>)}
                  </div>
                </div>
              </div>)}
            <div className="row mb-3 mt-4">
              <div className="col-12">
                <p>The Reason and Comment entered below will be visible to any other users within your organization with the Termination Viewer or Termination Provider Admin role.</p>
              </div>
            </div>
            <div className="row mb-3 col-6">
              <QESSelect
                label="Reason"
                disabled={viewMode || (!userIsTermAdmin && userIsTermViewer)}
                onChange={(e) => setReason(e.target.value)}
                value={reason}
                placeholder=""
                testId="termination-flyout__reason"
                required={true}
                isValid={!!reason}
              >
                {reasons.map((o) => <option key={o} value={o}>{o}</option>)}
              </QESSelect>
            </div>

            <div className="row mb-3">
              <QESTextArea
                containerClassName="col-6"
                label="Comment"
                testId="termination-flyout__comment"
                disabled={viewMode}
                maxLength={maxLength}
                onChange={(e) => setComment(e.target.value)}
                value={comment}
                required={reason === "Other"}
                isValid={!!comment}
              />
            </div>

            <FlyoutFooter
              submitButton={<QESButton qesType="primary" qesTestId="termination-flyout__submit" onClick={handleSubmit}
                disabled={viewMode || !reason || !identifier || (reason === "Other" && !comment) || isSubmitting}>Submit</QESButton>}
            />
          </>
        )
      }
    </>
  );
};
const WrappedTerminateProvidersPanel = withFlyoutContext(withUserContext(TerminateProvidersPanel));
export { WrappedTerminateProvidersPanel, getDefaultIdentifier };